import { useState, useEffect } from 'react';
import { authFetch } from '@/utils/authFetch';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Calendar, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Leaves() {
  const [leaves, setLeaves] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    days: 1,
    reason: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leavesRes, userRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/hr/leaves`),
        authFetch(`${BACKEND_URL}/api/auth/me`)
      ]);
      if (leavesRes.ok) setLeaves(await leavesRes.json());
      if (userRes.ok) setCurrentUser(await userRes.json());
    } catch (error) {
      toast.error('Failed to load leaves');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${BACKEND_URL}/api/hr/leaves`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success('Leave request submitted');
        setDialogOpen(false);
        setFormData({ leave_type: 'casual', start_date: '', end_date: '', days: 1, reason: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to submit leave request');
    }
  };

  const handleStatusUpdate = async (leaveId, status) => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/hr/leaves/${leaveId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success(`Leave ${status}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update leave status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-700 border-amber-100',
      approved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      rejected: 'bg-red-50 text-red-700 border-red-100'
    };
    return styles[status] || styles.pending;
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Leave Management</h1>
            <p className="text-slate-600 mt-1">Request and manage employee leaves</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700"><Plus className="w-4 h-4 mr-2" />Request Leave</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Request Leave</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><Label>Leave Type</Label><Select value={formData.leave_type} onValueChange={(v) => setFormData({...formData, leave_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="sick">Sick Leave</SelectItem><SelectItem value="casual">Casual Leave</SelectItem><SelectItem value="vacation">Vacation</SelectItem></SelectContent></Select></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Start Date</Label><Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} required /></div>
                  <div><Label>End Date</Label><Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} required /></div>
                </div>
                <div><Label>Number of Days</Label><Input type="number" min="1" value={formData.days} onChange={(e) => setFormData({...formData, days: parseInt(e.target.value)})} required /></div>
                <div><Label>Reason</Label><Textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} required rows={3} /></div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Submit Request</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {leaves.map((leave) => (
            <Card key={leave.leave_id} className="card-hover">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{leave.employee_name} - {leave.leave_type}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">{leave.start_date} to {leave.end_date} ({leave.days} days)</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(leave.status)}`}>
                    {leave.status}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-600">{leave.reason}</p>
                {currentUser?.role === 'admin' && leave.status === 'pending' && (
                  <div className="flex space-x-2 pt-2 border-t">
                    <Button size="sm" onClick={() => handleStatusUpdate(leave.leave_id, 'approved')} className="flex-1 bg-emerald-600 hover:bg-emerald-700"><Check className="w-4 h-4 mr-1" />Approve</Button>
                    <Button size="sm" onClick={() => handleStatusUpdate(leave.leave_id, 'rejected')} className="flex-1 bg-red-600 hover:bg-red-700"><X className="w-4 h-4 mr-1" />Reject</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}